import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import BarraRicerca from "../features/BarraRicerca";

// Ingresso a cascata: gli elementi salgono dal basso uno dopo l'altro.
const contenitore = {
  nascosto: {},
  visibile: {
    transition: { staggerChildren: 0.18, delayChildren: 0.2 },
  },
};

const salita = {
  nascosto: { opacity: 0, y: 40 },
  visibile: {
    opacity: 1,
    y: 0,
    transition: { duration: 1.05, ease: [0.22, 1, 0.36, 1] },
  },
};

const Hero = () => {
  const { t } = useTranslation();
  const varianti = salita;

  return (
    <div className="relative md:absolute w-full md:h-full z-0" style={{ background: "var(--color-dark)" }}>
      <div className="bg-opacity-50 opacity-35 z-0">
        <img
          src="/hero.webp"
          srcSet="/hero-800.webp 800w, /hero-1400.webp 1400w, /hero.webp 2000w"
          sizes="100vw"
          alt="New Moon Rooms, La Spezia"
          className="absolute w-full h-full object-cover"
          loading="eager"
          fetchPriority="high"
        />
      </div>
      <div
        aria-hidden="true"
        className="absolute inset-0 z-[1] pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 65% 50% at 50% 112%, rgba(201,169,98,0.20), transparent 65%)",
        }}
      />
      <motion.div
        className="relative md:absolute md:top-0 md:left-0 w-full md:h-full flex flex-col items-center justify-center z-10 pt-28 pb-24 md:pt-0 md:pb-0"
        variants={contenitore}
        initial="nascosto"
        animate="visibile"
      >
        <motion.div variants={varianti} className="text-center">
          <h1 className="cormorant uppercase tracking-[0.14em] md:tracking-[0.18em] text-[#f5f1e8] text-[30px] md:text-[58px] font-medium leading-tight">
            {t("hero.title1")}
          </h1>
        </motion.div>
        <motion.div variants={varianti} className="text-center">
          <h1 className="cormorant uppercase tracking-[0.14em] md:tracking-[0.18em] italic text-[#e8d5a8] text-[30px] md:text-[58px] font-normal leading-tight">
            {t("hero.title2")}
          </h1>
        </motion.div>
        <motion.p
          variants={varianti}
          className="text-[#e3caa0] text-[18px] md:text-[26px] font-semibold tracking-wide text-center mt-5 px-6"
          style={{ textShadow: "0 2px 8px rgba(0,0,0,0.6)" }}
        >
          {t("hero.claim")}
        </motion.p>
        <motion.p
          variants={varianti}
          className="text-white/75 text-[15px] md:text-[18px] text-center mt-3 px-6 tracking-wide"
          style={{ textShadow: "0 2px 8px rgba(0,0,0,0.7)" }}
        >
          {t("hero.tagline")}
        </motion.p>
        <motion.div variants={varianti} className="w-full max-w-4xl px-4 mt-6 md:mt-8 relative z-40">
          <BarraRicerca ombra />
        </motion.div>
        <motion.p
          variants={varianti}
          className="mt-8 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 px-6 max-w-full text-center text-[#e3caa0] text-sm md:text-base tracking-[0.08em]"
          style={{ textShadow: "0 2px 8px rgba(0,0,0,0.7)" }}
        >
          <span><span className="cormorant text-lg md:text-2xl font-semibold">9.0</span> Excellent</span>
          <span aria-hidden="true" className="hidden sm:block w-px h-5 bg-[#c9a962]/50" />
          <span>761 reviews on Booking.com</span>
        </motion.p>
      </motion.div>
      <div className="absolute bottom-0 left-0 w-full z-[5] bg-black/50 backdrop-blur-sm py-4 px-6">
        <div className="flex flex-row items-center justify-center gap-3 sm:gap-10">
          <div className="flex items-center gap-1.5 sm:gap-2 text-white">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 sm:w-4 sm:h-4 shrink-0 text-lux-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="text-[9px] md:text-[14px] tracking-wide"><span className="md:hidden">La Spezia · CT</span><span className="hidden md:inline">{t("hero.badge1")}</span></span>
          </div>
          <div className="w-px h-4 bg-white opacity-30" />
          <div className="flex items-center gap-1.5 sm:gap-2 text-white">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 sm:w-4 sm:h-4 shrink-0 text-lux-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
            <span className="text-[9px] md:text-[14px] tracking-wide"><span className="md:hidden">Check-in 24/7</span><span className="hidden md:inline">{t("hero.badge2")}</span></span>
          </div>
          <div className="w-px h-4 bg-white opacity-30" />
          <div className="flex items-center gap-1.5 sm:gap-2 text-white">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 sm:w-4 sm:h-4 shrink-0 text-lux-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 3h12l4 6-10 13L2 9z" />
              <path d="M11 3 8 9l4 13 4-13-3-6" />
              <path d="M2 9h20" />
            </svg>
            <span className="text-[9px] md:text-[14px] tracking-wide"><span className="md:hidden">{t("hero.badge3Short")}</span><span className="hidden md:inline">{t("hero.badge3")}</span></span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hero;
